import React, { useState, useEffect, useCallback } from 'react';

const InputSheetTable = ({ inputSheet, onSheetChange, disabled }) => {
  const [localSheet, setLocalSheet] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (inputSheet) {
      const copy = JSON.parse(JSON.stringify(inputSheet));
      setLocalSheet(copy);
      const cats = new Set(
        (copy.input_parameters || []).map(p => p.category || 'Other')
      );
      setExpandedCategories(cats);
    }
  }, [inputSheet]);

  const updateParam = useCallback((idx, field, value) => {
    setLocalSheet(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (field === 'value') {
        const num = parseFloat(value);
        updated.input_parameters[idx].value = isNaN(num) ? value : num;
      } else {
        updated.input_parameters[idx][field] = value;
      }
      if (onSheetChange) onSheetChange(updated);
      return updated;
    });
  }, [onSheetChange]);

  const updateMetadata = useCallback((field, value) => {
    setLocalSheet(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated.metadata) updated.metadata = {};
      updated.metadata[field] = value;
      if (onSheetChange) onSheetChange(updated);
      return updated;
    });
  }, [onSheetChange]);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  if (!localSheet) return null;

  const params = localSheet.input_parameters || [];
  const metadata = localSheet.metadata || {};

  const categories = {};
  params.forEach((p, idx) => {
    const cat = p.category || 'Other';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push({ ...p, _idx: idx });
  });

  const filteredCategories = {};
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    Object.entries(categories).forEach(([cat, items]) => {
      const filtered = items.filter(p =>
        (p.label || '').toLowerCase().includes(term) ||
        String(p.value).toLowerCase().includes(term) ||
        (p.unit || '').toLowerCase().includes(term) ||
        (p.notes || '').toLowerCase().includes(term)
      );
      if (filtered.length > 0) filteredCategories[cat] = filtered;
    });
  } else {
    Object.assign(filteredCategories, categories);
  }

  const metaFields = [
    { key: 'model_type', label: 'Model Type' },
    { key: 'indication', label: 'Indication' },
    { key: 'intervention', label: 'Intervention' },
    { key: 'time_horizon', label: 'Time Horizon' },
    { key: 'cycle_length', label: 'Cycle Length' },
    { key: 'discount_rate_costs', label: 'Discount Rate (Costs)' },
    { key: 'discount_rate_effects', label: 'Discount Rate (Effects)' },
    { key: 'population', label: 'Population' },
    { key: 'perspective', label: 'Perspective' },
    { key: 'currency', label: 'Currency' },
    { key: 'country', label: 'Country' },
    { key: 'starting_age', label: 'Starting Age' },
    { key: 'cohort_size', label: 'Cohort Size' },
    { key: 'psa_iterations', label: 'PSA Iterations' },
  ];

  const EditableCell = ({ value, onSave, type = 'text', className = '' }) => {
    const [editing, setEditing] = useState(false);
    const [tempVal, setTempVal] = useState(String(value ?? ''));

    const commit = () => {
      setEditing(false);
      if (tempVal !== String(value ?? '')) {
        onSave(tempVal);
      }
    };

    if (disabled) {
      return <span className={className}>{value ?? ''}</span>;
    }

    if (editing) {
      return (
        <input
          className="isht-cell-input"
          type={type}
          value={tempVal}
          onChange={e => setTempVal(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          autoFocus
        />
      );
    }

    return (
      <span
        className={`isht-cell-editable ${className}`}
        onClick={() => { setEditing(true); setTempVal(String(value ?? '')); }}
        title="Click to edit"
      >
        {value ?? <span className="isht-cell-empty">-</span>}
      </span>
    );
  };

  return (
    <div className="isht-container">
      {/* Header */}
      <div className="isht-header">
        <div className="isht-header-left">
          <span className="isht-title">
            <i className="bi bi-table"></i> Input Parameters
          </span>
          <span className="isht-badge">{params.length} parameters</span>
        </div>
        <div className="isht-header-right">
          <div className="isht-search-wrap">
            <i className="bi bi-search isht-search-icon"></i>
            <input
              className="isht-search"
              type="text"
              placeholder="Search parameters..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Metadata Section */}
      <div className="isht-meta-section">
        <div className="isht-meta-grid">
          {metaFields.map(({ key, label }) => {
            const val = metadata[key];
            if (!val && val !== 0) return null;
            return (
              <div key={key} className="isht-meta-item">
                <span className="isht-meta-label">{label}</span>
                <EditableCell
                  value={val}
                  onSave={(v) => updateMetadata(key, v)}
                  className="isht-meta-value"
                />
              </div>
            );
          })}
          {metadata.health_states && metadata.health_states.length > 0 && (
            <div className="isht-meta-item isht-meta-wide">
              <span className="isht-meta-label">Health States</span>
              <span className="isht-meta-value">{metadata.health_states.join(', ')}</span>
            </div>
          )}
          {metadata.comparators && metadata.comparators.length > 0 && (
            <div className="isht-meta-item isht-meta-wide">
              <span className="isht-meta-label">Comparators</span>
              <span className="isht-meta-value">{metadata.comparators.join(', ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Parameters Table */}
      <div className="isht-table-wrap">
        <table className="isht-table">
          <thead>
            <tr>
              <th className="isht-th-param">Parameter</th>
              <th className="isht-th-value">Value</th>
              <th className="isht-th-unit">Unit</th>
              <th className="isht-th-notes">Notes</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(filteredCategories).map(([cat, items]) => (
              <React.Fragment key={cat}>
                <tr
                  className="isht-cat-row"
                  onClick={() => toggleCategory(cat)}
                >
                  <td colSpan={4}>
                    <i className={`bi bi-chevron-${expandedCategories.has(cat) ? 'down' : 'right'} isht-cat-chevron`}></i>
                    <span className="isht-cat-label">{cat}</span>
                    <span className="isht-cat-count">{items.length}</span>
                  </td>
                </tr>
                {expandedCategories.has(cat) && items.map((p) => {
                  const arm = p.model_context?.arm;
                  return (
                    <tr key={p._idx} className="isht-param-row">
                      <td className="isht-td-param">
                        {p.label}{arm ? <span className="isht-arm-tag">{arm}</span> : null}
                      </td>
                      <td className="isht-td-value">
                        <EditableCell
                          value={p.value}
                          onSave={(v) => updateParam(p._idx, 'value', v)}
                          className="isht-value-cell"
                        />
                      </td>
                      <td className="isht-td-unit">
                        <EditableCell
                          value={p.unit}
                          onSave={(v) => updateParam(p._idx, 'unit', v)}
                        />
                      </td>
                      <td className="isht-td-notes">
                        <EditableCell
                          value={p.notes}
                          onSave={(v) => updateParam(p._idx, 'notes', v)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InputSheetTable;
